import React, { useState } from 'react';
import { usePrdStore } from '../store/prd-store';
import { AiSuggestion } from '../types';

export const AiAssistant: React.FC = () => {
  const {
    aiSuggestions,
    selectedSectionId,
    selectedRequirementId,
    selectedUserStoryId,
    requestAiSuggestion,
    applyAiSuggestion,
    dismissAiSuggestion,
  } = usePrdStore();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateSuggestion = async (type: AiSuggestion['type']) => {
    setIsGenerating(true);
    const targetId = selectedSectionId || selectedRequirementId || selectedUserStoryId;
    await requestAiSuggestion(type, targetId || undefined);
    setIsGenerating(false);
  };

  return (
    <div className="prd-ai-assistant w-80 border-l bg-white dark:bg-gray-800 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">
          AI Assistant
        </h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className={`w-5 h-5 transition-transform ${isExpanded ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <>
          {/* Quick Actions */}
          <div className="p-4 border-b">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Generate Content
            </h4>
            <div className="space-y-2">
              <button
                onClick={() => handleGenerateSuggestion('requirement')}
                disabled={isGenerating || !selectedSectionId}
                className="w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Suggest Requirements</span>
                </div>
              </button>
              
              <button
                onClick={() => handleGenerateSuggestion('user-story')}
                disabled={isGenerating}
                className="w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Generate User Stories</span>
                </div>
              </button>
              
              <button
                onClick={() => handleGenerateSuggestion('acceptance-criteria')}
                disabled={isGenerating || (!selectedRequirementId && !selectedUserStoryId)}
                className="w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <span>Acceptance Criteria</span>
                </div>
              </button>
              
              <button
                onClick={() => handleGenerateSuggestion('risk')}
                disabled={isGenerating}
                className="w-full text-left px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Identify Risks</span>
                </div>
              </button>
            </div>
          </div>

          {/* Suggestions */}
          <div className="flex-1 overflow-y-auto">
            {isGenerating && (
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-500"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Generating suggestions...
                  </span>
                </div>
              </div>
            )}

            {aiSuggestions.length > 0 ? (
              <div className="p-4 space-y-3">
                {aiSuggestions.map(suggestion => (
                  <div
                    key={suggestion.id}
                    className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">
                        {suggestion.type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {Math.round(suggestion.confidence * 100)}% confidence
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      {suggestion.content}
                    </p>
                    
                    {suggestion.reasoning && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 italic">
                        {suggestion.reasoning}
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => applyAiSuggestion(suggestion.id)}
                        className="flex-1 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => dismissAiSuggestion(suggestion.id)}
                        className="flex-1 px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : !isGenerating && (
              <div className="p-4 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click a button above to generate AI suggestions
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};